class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 250);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static setLoadingState(isLoading) {
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.toggle('hidden', !isLoading));

    document
      .querySelector('#ProductGridContainer .collection')
      ?.classList.toggle('loading', isLoading);

    document.getElementById('ProductCount')?.classList.toggle('loading', isLoading);
    document.getElementById('ProductCountDesktop')?.classList.toggle('loading', isLoading);
  }

  static cancelActiveRequest() {
    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
      FacetFiltersForm.abortController = null;
    }
  }

static renderPage(searchParams, event, updateURLHash = true) {
  const params = new URLSearchParams(searchParams);
  const currentUrl = new URL(window.location.href);

  ['viewtype'].forEach((key) => {
    if (currentUrl.searchParams.has(key) && !params.has(key)) {
      params.set(key, currentUrl.searchParams.get(key));
    }
  });

  const finalSearchParams = params.toString();

  FacetFiltersForm.searchParamsPrev = finalSearchParams;
  const sections = FacetFiltersForm.getSections();
  const requestId = ++FacetFiltersForm.requestId;

  FacetFiltersForm.cancelActiveRequest();
  FacetFiltersForm.setLoadingState(true);

  // Tell infinite-scroll.js to cancel any page request for the old grid.
  document.dispatchEvent(new CustomEvent('facets:before-render'));

  sections.forEach((section) => {
    const url = `${window.location.pathname}?section_id=${section.section}&${finalSearchParams}`;
    const filterDataUrl = (element) => element.url === url;

    FacetFiltersForm.filterData.some(filterDataUrl)
      ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId)
      : FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
  });

  if (updateURLHash) FacetFiltersForm.updateURLHash(finalSearchParams);
}

  static async renderSectionFromFetch(url, event, requestId) {
    const controller = new AbortController();
    FacetFiltersForm.abortController = controller;

    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, FacetFiltersForm.requestTimeout);

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Filter request failed with HTTP ${response.status}`);

      const html = await response.text();
      if (requestId !== FacetFiltersForm.requestId) return;

      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

      FacetFiltersForm.filterData = [
        ...FacetFiltersForm.filterData.filter((item) => item.url !== url),
        { html, url },
      ].slice(-FacetFiltersForm.cacheLimit);

      FacetFiltersForm.renderSection(parsedHTML, event);
    } catch (error) {
      if (error.name === 'AbortError') {
        if (timedOut && requestId === FacetFiltersForm.requestId) {
          console.warn(`Filter request timed out after ${FacetFiltersForm.requestTimeout}ms`);
        }
      } else {
        console.error('Filter request failed:', error);
      }
    } finally {
      window.clearTimeout(timeoutId);

      if (FacetFiltersForm.abortController === controller) {
        FacetFiltersForm.abortController = null;
      }

      if (requestId === FacetFiltersForm.requestId) {
        FacetFiltersForm.setLoadingState(false);
      }
    }
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    try {
      const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

      if (requestId !== FacetFiltersForm.requestId) return;
      FacetFiltersForm.renderSection(parsedHTML, event);
    } catch (error) {
      console.error('Cached filter render failed:', error);
    } finally {
      if (requestId === FacetFiltersForm.requestId) {
        FacetFiltersForm.setLoadingState(false);
      }
    }
  }

  static renderSection(parsedHTML, event) {
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }
  }

  static renderProductGridContainer(parsedHTML) {
    const target = document.getElementById('ProductGridContainer');
    const source = parsedHTML.getElementById('ProductGridContainer');

    if (!target || !source) throw new Error('ProductGridContainer is missing from the filter response');

    target.innerHTML = source.innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(parsedHTML) {
    const source = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (!source || !container) return;

    const count = source.innerHTML;
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

static updateURLHash(searchParams) {
  const currentUrl = new URL(window.location.href);
  const newUrl = new URL(window.location.pathname, window.location.origin);
  const params = new URLSearchParams(searchParams);

  ['viewtype'].forEach((key) => {
    if (currentUrl.searchParams.has(key) && !params.has(key)) {
      params.set(key, currentUrl.searchParams.get(key));
    }
  });

  const queryString = params.toString();
  history.pushState(
    { searchParams: queryString },
    '',
    `${window.location.pathname}${queryString ? '?' + queryString : ''}`
  );
}

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
     const params = new URLSearchParams(new FormData(form));

  for (const [key, value] of [...params.entries()]) {
    if (!value.trim()) {
      params.delete(key);
    }
  }

  return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.abortController = null;
FacetFiltersForm.requestId = 0;
FacetFiltersForm.requestTimeout = 8000;
FacetFiltersForm.cacheLimit = 20;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
  event.preventDefault();

  const link = event.currentTarget;
  if (!link) return;

  // 🔥 FORCE redirect for clear-all
  if (link.hasAttribute('data-clear-all-link')) {
    window.location.href = link.href;
    return;
  }

  // Existing logic
  if (link.hasAttribute('data-collection-path-switch')) {
    const currentUrl = new URL(window.location.href);
    const targetUrl = new URL(link.href, window.location.origin);

    const currentViewType = currentUrl.searchParams.get('viewtype');

    if (currentViewType) {
      targetUrl.searchParams.set('viewtype', currentViewType);
    } else {
      targetUrl.searchParams.delete('viewtype');
    }

    targetUrl.searchParams.delete('page');

    window.location.href = targetUrl.toString();
    return;
  }

  const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
  form?.onActiveFilterClick(event);
}
}

customElements.define('facet-remove', FacetRemove);
